import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form-v7';
import {
  Button,
  Checkbox,
  Input,
  Label,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@parrot-software/design-system';
import { CupSoda, CircleHelp, Plus, Trash2, Utensils } from '@parrot-software/design-system/icons';

import { RadioCardGrid, StepLayout } from '@onboarding/presentation/components/shared';
import type { WizardFormValues } from '@onboarding/presentation/types';

const EMPTY_AREA = { name: '', tableCount: '', prefix: '' };

const generatePrefix = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');

/**
 * Step 2 of the onboarding wizard: Table service selection with area configuration.
 * Renders two selectable cards for service type plus an area config form when table service is selected.
 */
export const TableServiceStep = () => {
  const { t } = useTranslation('onboarding');
  const { control, setValue, getValues } = useFormContext<WizardFormValues>();

  const { fields, append, remove, replace } = useFieldArray({ control, name: 'tableService.areas' });

  const hasTableService = useWatch({ control, name: 'tableService.hasTableService' });
  const skipAreaConfig = useWatch({ control, name: 'tableService.skipAreaConfig' });
  const watchedAreas = useWatch({ control, name: 'tableService.areas' });

  const serviceOptions = useMemo(
    () => [
      {
        value: 'true',
        label: t('steps.tableService.options.withService'),
        description: t('steps.tableService.options.withServiceDescription'),
        icon: <Utensils className="text-primary" size={24} />,
      },
      {
        value: 'false',
        label: t('steps.tableService.options.withoutService'),
        description: t('steps.tableService.options.withoutServiceDescription'),
        icon: <CupSoda className="text-primary" size={24} />,
      },
    ],
    [t],
  );

  // Sync areas with service mode and skip checkbox:
  // - table service OFF → clear rows
  // - table service ON + skip OFF → ensure at least one row
  // - table service ON + skip ON → clear rows (user will configure later)
  useEffect(() => {
    const areas = getValues('tableService.areas');

    if (hasTableService === 'false' && areas.length > 0) {
      replace([]);
      return;
    }

    if (hasTableService === 'true' && !skipAreaConfig && areas.length === 0) {
      replace([{ ...EMPTY_AREA }]);
    }
  }, [hasTableService, skipAreaConfig, getValues, replace]);

  const showAreaConfig = hasTableService === 'true';
  const isLastAreaComplete =
    watchedAreas.length > 0 &&
    !!watchedAreas[watchedAreas.length - 1]?.name?.trim() &&
    !!watchedAreas[watchedAreas.length - 1]?.tableCount?.trim();
  const hasAnyAreaData = watchedAreas.some((area) => !!area?.name?.trim() || !!area?.tableCount?.trim());

  return (
    <StepLayout description={t('steps.tableService.description')} title={t('steps.tableService.title')}>
      <Controller
        control={control}
        name="tableService.hasTableService"
        render={({ field }) => (
          <RadioCardGrid columns={2} onChange={field.onChange} options={serviceOptions} value={field.value} />
        )}
      />

      {showAreaConfig && (
        <div className="flex flex-col gap-5">
          <p className="text-base font-semibold leading-6 text-foreground">{t('steps.tableService.areas.title')}</p>

          <div className={cn('flex flex-col items-end gap-5', skipAreaConfig && 'opacity-50')}>
            {fields.map((rowField, index) => (
              <div
                className="grid w-full grid-cols-1 items-start gap-4 md:grid-cols-[320px_1fr_1fr_auto]"
                key={rowField.id}
              >
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1">
                    <Label className="whitespace-nowrap text-sm font-medium text-foreground">
                      {t('steps.tableService.areas.areaName')}
                      <span className="text-destructive"> *</span>
                    </Label>
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button className="inline-flex cursor-help" type="button">
                            <CircleHelp className="h-4 w-4 text-info" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[256px] border bg-background text-foreground shadow-md">
                          <p>{t('steps.tableService.areas.areaNameTooltip')}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Controller
                    control={control}
                    name={`tableService.areas.${index}.name`}
                    render={({ field, fieldState }) => (
                      <>
                        <Input
                          className={cn({ 'border-destructive': fieldState.error })}
                          disabled={skipAreaConfig}
                          onBlur={field.onBlur}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            setValue(`tableService.areas.${index}.prefix`, generatePrefix(e.target.value));
                          }}
                          placeholder={t('steps.tableService.areas.areaNamePlaceholder')}
                          value={field.value}
                        />
                        <p className="min-h-[16px] text-xs text-destructive">{fieldState.error?.message ?? ''}</p>
                      </>
                    )}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <Label className="whitespace-nowrap text-sm font-medium text-foreground">
                    {t('steps.tableService.areas.tableCount')}
                    <span className="text-destructive"> *</span>
                  </Label>
                  <Controller
                    control={control}
                    name={`tableService.areas.${index}.tableCount`}
                    render={({ field, fieldState }) => (
                      <>
                        <Input
                          className={cn({ 'border-destructive': fieldState.error })}
                          disabled={skipAreaConfig}
                          max={99}
                          min={1}
                          onBlur={field.onBlur}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
                            field.onChange(raw);
                          }}
                          onKeyDown={(e) => {
                            if (['e', 'E', '+', '-', '.', ','].includes(e.key)) e.preventDefault();
                          }}
                          placeholder={t('steps.tableService.areas.tableCountPlaceholder')}
                          type="number"
                          value={field.value}
                        />
                        <p className="min-h-[16px] text-xs text-destructive">{fieldState.error?.message ?? ''}</p>
                      </>
                    )}
                  />
                </div>

                <div className="flex items-end gap-4">
                  <div className="flex flex-1 flex-col gap-1">
                    <Label className="whitespace-nowrap text-sm font-medium text-foreground">
                      {t('steps.tableService.areas.prefix')}
                      <span className="text-destructive"> *</span>
                    </Label>
                    <Controller
                      control={control}
                      name={`tableService.areas.${index}.prefix`}
                      render={({ field }) => (
                        <>
                          <Input
                            disabled
                            onChange={(e) => field.onChange(e.target.value)}
                            placeholder={t('steps.tableService.areas.prefixPlaceholder')}
                            value={field.value}
                          />
                          <p className="min-h-[16px]" />
                        </>
                      )}
                    />
                  </div>

                  <div className="mb-[16px] shrink-0 md:hidden">
                    <Button
                      className={cn('size-8 p-2', (fields.length <= 1 || skipAreaConfig) && 'opacity-50')}
                      disabled={fields.length <= 1 || skipAreaConfig}
                      onClick={() => remove(index)}
                      type="button"
                      variant="outline"
                    >
                      <Trash2 className="text-destructive" size={16} />
                    </Button>
                  </div>
                </div>

                <div className="hidden pt-6 md:block">
                  <Button
                    className={cn('size-8 p-2', (fields.length <= 1 || skipAreaConfig) && 'opacity-50')}
                    disabled={fields.length <= 1 || skipAreaConfig}
                    onClick={() => remove(index)}
                    type="button"
                    variant="outline"
                  >
                    <Trash2 className="text-destructive" size={16} />
                  </Button>
                </div>
              </div>
            ))}

            <Button
              className={cn('w-full gap-2 md:w-auto', (!isLastAreaComplete || skipAreaConfig) && 'opacity-50')}
              disabled={!isLastAreaComplete || skipAreaConfig}
              onClick={() => append({ ...EMPTY_AREA })}
              type="button"
              variant="outline"
            >
              <Plus size={16} />
              {t('steps.tableService.areas.addArea')}
            </Button>
          </div>

          <div className="flex flex-col gap-4">
            <Separator />
            <div className="flex items-center gap-2">
              <Controller
                control={control}
                name="tableService.skipAreaConfig"
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    disabled={hasAnyAreaData}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                  />
                )}
              />
              <Label className="text-sm font-medium text-foreground">
                {t('steps.tableService.areas.skipCheckbox')}
              </Label>
            </div>
          </div>
        </div>
      )}
    </StepLayout>
  );
};
